import { useQuery } from '@tanstack/react-query'
import { Col, Flex, Row } from 'antd'
import { useNavigate } from 'react-router-dom'

import {
  organizationsAccessSessionsListOptions,
  organizationsAccessSignInActivityRetrieveOptions,
  type UserRetrieve
} from 'src/api/main'
import {
  UserIdentityCard,
  UserQuickStats,
  UserRecentActivityCard,
  UserWorkspaceAccessCard
} from 'src/features/users/components'
import { useUserAppAccess } from 'src/features/users/hooks/useUserAppAccess'
import type { WorkspaceUser } from 'src/features/users/types'
import { paths } from 'src/router'

type Props = {
  profile: UserRetrieve
  record?: WorkspaceUser
  workspaceId?: number
  photoSrc?: string
  onEdit?: () => void
  canManageMembership?: boolean
  /** Recent activity and sign-in metrics need `audit.view`. */
  canViewAudit?: boolean
  /** The apps-used metric reads the workspace apps — `applications.view`. */
  canViewApps?: boolean
}

export const AccountProfileOverviewTab = ({
  profile,
  record,
  workspaceId,
  onEdit,
  canManageMembership = false,
  canViewAudit = true,
  canViewApps = true
}: Props) => {
  const navigate = useNavigate()
  const userId = profile.id
  const workspaceEnabled = Boolean(record && workspaceId && workspaceId > 0)

  const { grantedCount } = useUserAppAccess({
    workspaceId: workspaceId ?? 0,
    userId,
    enabled: workspaceEnabled && canViewApps
  })

  // The per-member sessions endpoint is an administrative one (`users.manage`),
  // even when the member is you — your own devices live on the Sessions tab.
  const { data: sessions } = useQuery({
    ...organizationsAccessSessionsListOptions({
      path: { id: record?.id ?? 0 }
    }),
    enabled: workspaceEnabled && canManageMembership
  })

  // Sign-in activity is served by the audit endpoint, so without `audit.view`
  // the request only comes back 403 — and the tile it feeds is dropped anyway.
  const { data: signInActivity } = useQuery({
    ...organizationsAccessSignInActivityRetrieveOptions({
      path: { id: record?.id ?? 0 }
    }),
    enabled: workspaceEnabled && canViewAudit
  })

  return (
    <Row gutter={[14, 14]}>
      <Col xs={24} lg={16}>
        <Flex vertical gap={14}>
          <UserIdentityCard
            user={record?.user ?? profile}
            jobTitle={record?.job_title}
            onEdit={onEdit}
          />
          {record && workspaceId ? (
            <UserWorkspaceAccessCard
              record={record}
              workspaceId={workspaceId}
              editable={canManageMembership}
            />
          ) : null}
        </Flex>
      </Col>

      <Col xs={24} lg={8}>
        <Flex vertical gap={14}>
          {record && workspaceId ? (
            <>
              <UserQuickStats
                showSignIns={canViewAudit}
                showAppsUsed={canViewApps}
                showSessions={canManageMembership}
                appsUsed={grantedCount}
                sessions={sessions?.length}
                signIns30d={signInActivity?.sign_ins}
              />
              {canViewAudit ? (
                <UserRecentActivityCard
                  workspaceId={workspaceId}
                  userId={userId}
                  onSeeAll={() => navigate(paths.account.audit)}
                />
              ) : null}
            </>
          ) : null}
        </Flex>
      </Col>
    </Row>
  )
}
